import React from 'react';
import { t } from 'i18next';
import { cn } from '@Utils';

import { NotionBlock, RichTextSegment } from '@Models';
import { formatNotionDate, getTagColorClass } from './helpers';
import RichText from './RichText';

interface ChildDatabaseTableProps {
  block: NotionBlock;
  /**
   * When the database is the only thing on the page (the response from the
   * resources endpoint contained a single ``child_database`` block), let the
   * table grow with the viewport instead of capping at 600px. Embedded
   * databases inside a page keep the cap so they don't dominate the layout.
   */
  standalone?: boolean;
}

const ChildDatabaseTable = ({ block, standalone = false }: ChildDatabaseTableProps) => {
  const { rows } = block.content;

  if (!rows || rows.length === 0) {
    return (
      <div className="my-4 p-6 bg-gray-50 border border-dashed border-gray-700 rounded-lg text-gray-500 text-center">
        {t('Documentation.EmptyDataBase')}
      </div>
    );
  }

  const columnNames = Object.keys(rows[0].data || {}).reverse();

  const renderCellContent = (value: unknown): React.ReactNode => {
    if (value === null || value === undefined) {
      return null;
    }

    // Select, status, or object with name
    if (typeof value === 'object' && !Array.isArray(value) && 'name' in value && value.name !== undefined) {
      const itemName = (value as { name: string }).name;
      if (!itemName) return null;

      const colorClass = getTagColorClass((value as { name: string; color?: string }).color);
      return (
        <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', colorClass)}>
          {itemName}
        </span>
      );
    }

    // Multi-select or people array
    if (Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item: unknown, idx: number) => {
            if (typeof item === 'object' && item !== null && 'name' in item) {
              const colorClass = getTagColorClass((item as { name: string; color?: string }).color);
              return (
                <span
                  key={
                    (item as { name?: string; id?: string }).name ||
                    (item as { name?: string; id?: string }).id ||
                    String(idx)
                  }
                  className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', colorClass)}>
                  {String((item as { name: string }).name)}
                </span>
              );
            }
            return (
              <span
                key={String(item) + String(idx)}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                {String(item)}
              </span>
            );
          })}
        </div>
      );
    }

    // Date object
    if (typeof value === 'object' && value !== null && ('start' in value || 'date' in value)) {
      const dateObj = value as { start?: string; end?: string | null };
      if (dateObj.start) {
        const formattedStart = formatNotionDate(dateObj.start);
        return dateObj.end ? `${formattedStart} → ${formatNotionDate(dateObj.end)}` : formattedStart;
      }
      return null;
    }

    // Rich text
    if (typeof value === 'object' && 'text' in value && 'rich_content' in value) {
      return (
        <RichText
          segments={(value as { text: string; rich_content: RichTextSegment[] }).rich_content || []}
          defaultColor="text-black"
        />
      );
    }

    // Checkbox
    if (typeof value === 'boolean') {
      return <span className={cn('text-base', value ? 'text-green-600' : 'text-gray-700')}>{value ? '☑' : '☐'}</span>;
    }

    // Plain text, number, etc.
    return String(value);
  };

  return (
    <div className="my-6">
      {/* Database Title */}
      {block.content.title && <h4 className="text-lg font-medium text-gray-800 mb-3">{block.content.title}</h4>}

      {/* Database Table */}
      <div className={cn(standalone ? 'overflow-x-auto' : 'overflow-auto max-h-[600px]')}>
        <table className="min-w-full">
          <thead className="sticky top-0 z-10 bg-gray-200">
            <tr>
              {columnNames.map((columnName, index) => (
                <th
                  key={columnName}
                  className={`px-4 py-2.5 text-left text-sm font-medium text-gray-500 border-b border-gray-200 ${
                    index !== 0 ? 'border-l border-gray-200' : ''
                  }`}>
                  {columnName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={row.id} className="hover:bg-gray-200/50 transition-colors">
                {columnNames.map((columnName, colIndex) => (
                  <td
                    key={String(row.id) + columnName}
                    className={`px-4 py-3 text-sm text-gray-900 ${
                      rowIndex !== rows.length - 1 ? 'border-b border-gray-200' : ''
                    } ${colIndex !== 0 ? 'border-l border-gray-200' : ''}`}>
                    {renderCellContent(row.data?.[columnName])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChildDatabaseTable;
